import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';

/** Gruppierte Rechtsformen (deutsche Unternehmensarten). */
export const LEGAL_FORMS: { group: string; options: string[] }[] = [
  { group: 'Einzelunternehmen', options: ['Einzelunternehmer / Einzelkaufmann (e.K.)', 'Freiberufler', 'Kleingewerbetreibender'] },
  { group: 'Personengesellschaften', options: ['GbR (Gesellschaft bürgerlichen Rechts)', 'OHG (Offene Handelsgesellschaft)', 'KG (Kommanditgesellschaft)', 'PartG (Partnerschaftsgesellschaft)', 'PartG mbB'] },
  { group: 'Kapitalgesellschaften', options: ['GmbH (Gesellschaft mit beschränkter Haftung)', 'UG (haftungsbeschränkt)', 'AG (Aktiengesellschaft)', 'GmbH & Co. KG', 'UG & Co. KG'] },
  { group: 'Genossenschaften & Vereine', options: ['eG (eingetragene Genossenschaft)', 'e.V. (eingetragener Verein)'] },
  { group: 'Sonstige', options: ['Stiftung', 'Sonstige'] },
];

/** Branchen-Vorschläge für die KI-Belegkategorisierung (Freitext zulässig). */
export const INDUSTRIES: string[] = [
  'Handwerk – Elektro', 'Handwerk – Sanitär/Heizung', 'Handwerk – Bau/Maurer', 'Handwerk – Maler/Lackierer',
  'Handwerk – Tischler/Schreiner', 'Handwerk – KFZ/Werkstatt', 'Friseur / Kosmetik',
  'Gastronomie / Restaurant', 'Café / Bäckerei', 'Einzelhandel / Laden', 'Online-Handel / E-Commerce',
  'IT / Software-Entwicklung', 'IT-Beratung', 'Unternehmensberatung', 'Marketing / Werbung', 'Design / Kreativ',
  'Fotografie / Video', 'Architektur / Ingenieurbüro', 'Rechtsanwalt / Notar', 'Steuerberatung',
  'Arztpraxis / Heilberufe', 'Physiotherapie / Heilpraktik', 'Pflege / Soziales',
  'Bildung / Coaching / Training', 'Immobilien / Makler', 'Transport / Logistik', 'Reinigung / Gebäudeservice',
  'Landwirtschaft', 'Fitness / Sport', 'Eventmanagement', 'Sonstige Dienstleistung',
];

/**
 * Branchen-Mehrfachauswahl (für Mix-Unternehmen). Wert bleibt ein
 * kommaseparierter String — kompatibel zum vorhandenen `industry`-Feld.
 * Chips zum Entfernen + Dropdown/Freitext zum Hinzufügen.
 */
export function IndustryInput({ value, onChange }: { value: string; onChange: (v: string) => void }): ReactElement {
  const [text, setText] = useState('');
  const selected = value.split(',').map((s) => s.trim()).filter(Boolean);

  function add(raw: string) {
    const v = raw.trim();
    if (!v) return;
    // Doppelte (case-insensitive) vermeiden.
    if (selected.some((s) => s.toLowerCase() === v.toLowerCase())) {
      setText('');
      return;
    }
    onChange([...selected, v].join(', '));
    setText('');
  }
  function remove(item: string) {
    onChange(selected.filter((s) => s !== item).join(', '));
  }

  const remaining = INDUSTRIES.filter((i) => !selected.some((s) => s.toLowerCase() === i.toLowerCase()));

  return (
    <div className="rounded-xl border border-gray-300 p-2 transition focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200">
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((s) => (
            <span key={s} className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-sm font-medium text-blue-700">
              {s}
              <button type="button" onClick={() => remove(s)} aria-label={`${s} entfernen`} className="text-blue-500 hover:text-blue-800">
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          list="saldify-industries"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(text);
            }
          }}
          // Auswahl aus dem Datalist setzt den Wert -> direkt uebernehmen.
          onInput={(e) => {
            const v = (e.target as HTMLInputElement).value;
            if (INDUSTRIES.includes(v)) add(v);
          }}
          placeholder={selected.length ? 'Weitere Branche hinzufügen …' : 'z. B. Elektrohandwerk, IT-Beratung …'}
          className="flex-1 px-2 py-1.5 text-base outline-none"
        />
        {text.trim() && (
          <button type="button" onClick={() => add(text)} className="rounded-lg bg-blue-600 px-3 text-sm font-medium text-white hover:bg-blue-700">
            +
          </button>
        )}
      </div>
      <datalist id="saldify-industries">
        {remaining.map((i) => (
          <option key={i} value={i} />
        ))}
      </datalist>
    </div>
  );
}

/** Rechtsform-Auswahl (gruppiertes Dropdown). */
export function LegalFormSelect({ value, onChange }: { value: string; onChange: (v: string) => void }): ReactElement {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
    >
      <option value="">— Rechtsform wählen —</option>
      {LEGAL_FORMS.map((g) => (
        <optgroup key={g.group} label={g.group}>
          {g.options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * Hilfe-Badge "?" hinter wichtigen Feldern — Klick zeigt eine Erklaerung.
 * Fuer technikferne Nutzer: erklaert Begriffe direkt an Ort und Stelle.
 */
export function HelpBadge({ text }: { text: string }): ReactElement {
  const [open, setOpen] = useState(false);
  // Fixe Position relativ zum Button, am Viewport geclippt -> laeuft auf Mobile
  // nicht mehr aus dem Bildschirm.
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const margin = 8;
      const maxW = 256; // entspricht w-64
      const width = Math.min(maxW, window.innerWidth - margin * 2);
      // Mittig unter dem Badge, dann horizontal in den Viewport klemmen.
      let left = r.left + r.width / 2 - width / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
      setPos({ top: r.bottom + 6, left, width });
    }
    setOpen(true);
  }

  return (
    <span className="inline-block align-middle">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        aria-label="Hilfe"
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold text-gray-600 hover:bg-blue-100 hover:text-blue-700"
      >
        ?
      </button>
      {open && pos && (
        <span
          className="fixed z-50 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-snug text-white shadow-lg"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

/** Cent -> "1.234,56 €" */
export function euro(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: string | undefined;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
  busy,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  busy?: boolean;
}): ReactElement {
  const styles: Record<string, string> = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    ghost: 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${styles[variant]}`}
    >
      {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
      {children}
    </button>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }): ReactElement {
  return <div className={`rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 dark:bg-gray-800 dark:ring-gray-700 ${className}`}>{children}</div>;
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}): ReactElement {
  return (
    <Card className="text-center">
      <p className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</p>
      {hint && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </Card>
  );
}

/**
 * Zeigt einen Fehler an — bei 402 (Limit/Feature) mit freundlichem Upgrade-CTA,
 * damit technikferne Nutzer verstehen, was zu tun ist.
 */
export function ErrorNote({ error }: { error: unknown }): ReactElement | null {
  if (!error) return null;
  const isApi = error instanceof ApiError;
  const upgrade = isApi && error.status === 402;
  const msg = isApi ? error.message : 'Es ist ein Fehler aufgetreten.';
  return (
    <div
      role="alert"
      className={`rounded-xl px-4 py-3 text-sm ring-1 ${
        upgrade ? 'bg-amber-50 text-amber-800 ring-amber-100' : 'bg-red-50 text-red-700 ring-red-100'
      }`}
    >
      {msg}
      {upgrade && (
        <>
          {' '}
          <Link to="/preise" className="font-semibold underline">
            Tarif ansehen
          </Link>
        </>
      )}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }): ReactElement {
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Entwurf', cls: 'bg-gray-100 text-gray-700' },
    sent: { label: 'Versendet', cls: 'bg-blue-100 text-blue-700' },
    accepted: { label: 'Angenommen', cls: 'bg-green-100 text-green-700' },
    paid: { label: 'Bezahlt', cls: 'bg-green-100 text-green-700' },
    overdue: { label: 'Überfällig', cls: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Storniert', cls: 'bg-gray-100 text-gray-400 line-through' },
  };
  const s = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-700' };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

/** Einfaches Textfeld mit Label — grosse Touch-Targets, UX-freundlich. */
export function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  required,
  error,
  inputMode,
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  error?: boolean;
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email' | 'tel';
  step?: string;
}): ReactElement {
  const cls =
    'w-full rounded-xl border px-4 py-2.5 text-base outline-none transition focus:ring-2 ' +
    (error
      ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200');
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        step={step}
        className={cls}
      />
      {error ? (
        <span className="mt-1 block text-xs text-red-500">Bitte ausfüllen.</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{hint}</span>
      ) : null}
    </label>
  );
}

export function displayCustomer(c: {
  companyName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string {
  return c.companyName || `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || 'Ohne Namen';
}

/**
 * Kreis-Badge mit Initialen und stabiler Farbe. Wird in Listen als visueller
 * Anker verwendet (Belege, Rechnungen), damit Zeilen auf einen Blick
 * unterscheidbar sind. Die Farbe leitet sich aus dem Namen ab, ist also fuer
 * denselben Namen immer gleich.
 */
const BADGE_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-violet-100 text-violet-700', 'bg-rose-100 text-rose-700', 'bg-cyan-100 text-cyan-700',
  'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700',
];

export function badgeColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return BADGE_COLORS[h % BADGE_COLORS.length]!;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/** Initialen-Badge fuer Kunden/Empfaenger (ohne Logo-Aufloesung). */
export function PartyBadge({ name }: { name: string }): ReactElement {
  return (
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${badgeColor(name)}`}>
      {initials(name)}
    </span>
  );
}
